import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import L from 'leaflet';
import {
  func,
  arrayOf,
  objectOf,
  shape,
  string,
  object,
  any
} from 'prop-types';
import {
  Grid,
  TextField,
  Button,
  Chip,
  Card,
  CardContent,
  CardHeader,
  Divider
} from '@mui/material';

import AppsIcon from '@mui/icons-material/Apps';

import { EditControl } from 'react-leaflet-draw';

import { MapContainer, TileLayer, FeatureGroup } from 'react-leaflet';

// Actions
import {
  setId,
  setName,
  setGeoJson,
  getGeofences,
  newGeofence,
  updateGeofence,
  deleteGeofence,
  clearState
} from '../../actions/geofenceActions';

import Teleport from 'components/Teleport';

import {
  // walesCoords,
  dublinCoords
} from '../../constants';

import 'leaflet-draw/dist/leaflet.draw.css';
import { parseGeoJson } from '../../utils/parsingUtils';
import Page from '../../components/Page/Page';

const Geofences = ({
  id,
  name,
  geoJson,
  geofences,
  requestGeofences,
  doNewGeofence,
  doUpdateGeofence,
  doSetGeoJson,
  doSetId,
  doSetName,
  doClearState,
  doDeleteGeofence
}) => {
  const [createEnabled, setCreateEnabled] = useState(true);

  useEffect(() => {
    requestGeofences();
  }, [requestGeofences]);

  const drawFGRef = React.createRef();
  const mapRef = React.createRef();

  useEffect(() => {
    let leafletFG;
    if (drawFGRef.current) {
      leafletFG = drawFGRef.current.leafletElement;
    }
    if (leafletFG) {
      const parsed = parseGeoJson(geoJson);
      if (parsed) {
        const leafletGeoJSON = new L.GeoJSON(parsed);
        leafletFG.clearLayers();
        leafletGeoJSON.eachLayer(layer => {
          leafletFG.addLayer(layer);
        });
        mapRef.current.leafletElement.fitBounds(leafletFG.getBounds());
      } else {
        leafletFG.clearLayers();
      }
    }
  }, [geoJson, drawFGRef, mapRef]);

  const initialLocation = dublinCoords;

  return (
    <Page style={{ padding: '24px' }}>
      <Grid container spacing={3}>
        <Grid item xs={7}>
          <Card>
            <CardContent>
              <MapContainer
                center={initialLocation}
                style={{ height: '500px' }}
                zoom={16}
                maxZoom={24}
                ref={mapRef}
                animate>
                <TileLayer
                  attribution="Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community"
                  url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                />
                <FeatureGroup ref={drawFGRef}>
                  <EditControl
                    position="topright"
                    onEdited={e => {
                      const gJson = e.layers.toGeoJSON();
                      if (gJson.features.length > 0) {
                        doSetGeoJson(gJson);
                      }
                    }}
                    onCreated={e => {
                      setCreateEnabled(false);
                      doSetGeoJson(e.layer.toGeoJSON());
                    }}
                    onDeleted={() => setCreateEnabled(true)}
                    draw={{
                      polygon: createEnabled
                        ? { allowIntersection: false }
                        : false,
                      marker: false,
                      rectangle: false,
                      circle: false,
                      circlemarker: false,
                      polyline: false
                    }}
                  />
                </FeatureGroup>
              </MapContainer>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={5}>
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <Card>
                <CardHeader title="Locations" />
                <Divider />
                <CardContent>
                  <Teleport ref={mapRef} />
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12}>
              <Card>
                <CardHeader title="Geofences" />
                <Divider />
                <CardContent>
                  {geofences.geofences.map(
                    geof =>
                      geof.name && (
                        <Chip
                          style={{ margin: '3px' }}
                          icon={<AppsIcon />}
                          label={geof.name}
                          onClick={() => {
                            doSetId(geof.id);
                            doSetName(geof.name);
                            doSetGeoJson(geof.geoJson);
                          }}
                          key={geof.id}
                        />
                      )
                  )}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12}>
              <Card>
                <CardHeader title={id ? 'Edit Geofence' : 'New Geofence'} />
                <Divider />
                <CardContent>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <TextField
                        id="name"
                        disabled={!geoJson}
                        label={id ? 'Change name' : 'Name'}
                        value={name || ''}
                        onChange={e => doSetName(e.target.value)}
                        margin="normal"
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        color="primary"
                        style={{ margin: '8px' }}
                        onClick={() => {
                          if (id) {
                            doUpdateGeofence({ id, name, geoJson });
                            return;
                          }
                          doNewGeofence({ id, name, geoJson });
                        }}
                        disabled={!geoJson || !name}
                        variant="contained">
                        {id ? 'Update' : 'New'}
                      </Button>
                      <Button
                        color="secondary"
                        style={{ margin: '8px' }}
                        onClick={() => doClearState()}
                        variant="contained">
                        Clear
                      </Button>
                      <Button
                        color="secondary"
                        disabled={!geoJson || !name}
                        style={{ margin: '8px' }}
                        onClick={() => doDeleteGeofence({ id, name })}
                        variant="contained">
                        Delete
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Page>
  );
};

const mapStateToProps = state => ({
  geofences: state.geofences,
  id: state.geofences.id,
  name: state.geofences.name,
  geoJson: state.geofences.geoJson
});

const mapDispatchToProps = dispatch => ({
  requestGeofences: () => dispatch(getGeofences()),
  doNewGeofence: geofence => dispatch(newGeofence(geofence)),
  doUpdateGeofence: geofence => dispatch(updateGeofence(geofence)),
  doDeleteGeofence: geofence => dispatch(deleteGeofence(geofence)),
  doSetId: id => dispatch(setId(id)),
  doSetName: name => dispatch(setName(name)),
  doSetGeoJson: geoJson => dispatch(setGeoJson(geoJson)),
  doClearState: () => dispatch(clearState())
});

Geofences.propTypes = {
  geofences: shape({
    geofences: arrayOf(object),
    selected: objectOf(any)
  }),
  id: string,
  name: string,
  geoJson: objectOf(any),
  requestGeofences: func.isRequired,
  doNewGeofence: func.isRequired,
  doUpdateGeofence: func.isRequired,
  doSetId: func.isRequired,
  doSetName: func.isRequired,
  doSetGeoJson: func.isRequired,
  doClearState: func.isRequired,
  doDeleteGeofence: func.isRequired
};

Geofences.defaultProps = {
  geofences: [],
  id: '',
  name: '',
  geoJson: {}
};

export default connect(mapStateToProps, mapDispatchToProps)(Geofences);
